import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PlanAccordion from 'components/plan/PlanAccordion';

/**
 * Previsualización EN VIVO de un plan tal como lo verá el cliente (reusa PlanAccordion).
 * Se alimenta de los valores actuales del formulario (react-final-form FormSpy) y se actualiza
 * conforme se edita — estilo "crear hoja de vida".
 */
function PlanPreview({ values }) {
  const sessionTypeOptions = useSelector((state) => state.sessionType.sessionTypeOptions);

  // Imagen: si hay un File nuevo (recortado) → blob URL; si es string → úsalo; sino image_url.
  const imageUrl = useMemo(() => {
    if (values.image && typeof values.image !== 'string') return URL.createObjectURL(values.image);
    if (typeof values.image === 'string' && values.image) return values.image;
    return values.image_url || null;
  }, [values.image, values.image_url]);

  useEffect(
    () => () => {
      if (imageUrl && imageUrl.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl]
  );

  const sessionTypes = useMemo(() => {
    const ids = values.sessionTypeIds || [];
    return ids
      .map((id) => sessionTypeOptions.find((st) => st.value === id))
      .filter(Boolean)
      .map((st) => ({ id: st.value, name: st.label }));
  }, [values.sessionTypeIds, sessionTypeOptions]);

  const previewPlan = {
    id: 'preview',
    name: values.name || 'Nombre del plan',
    description: values.description || '',
    image_url: imageUrl,
    price_cop: values.price_cop,
    price_usd: values.price_usd,
    sessions: values.sessions,
    is_subscribable: !!values.is_subscribable,
    subscription_interval: values.is_subscribable ? 'Mensual' : null,
    session_types: sessionTypes
  };

  return (
    <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <VisibilityOutlinedIcon sx={{ fontSize: 18, color: 'gold.main' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          Vista previa · así lo verá el cliente
        </Typography>
      </Stack>
      <PlanAccordion plan={previewPlan} defaultExpanded />
    </Box>
  );
}

PlanPreview.propTypes = {
  values: PropTypes.object.isRequired
};

export default PlanPreview;
